import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, ArrowRight } from "lucide-react";

const money = (v, ccy) =>
  `${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;

/** Card top-ups: charged in the client's home currency, credited in USD. */
export const CardDeposit = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/deposit/card/options").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const pay = async (pkg) => {
    setBusy(pkg.package_id);
    try {
      const { data: res } = await api.post("/deposit/card/checkout", {
        package_id: pkg.package_id, origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error("Couldn't start the card payment", { description: e?.response?.data?.detail });
      setBusy(null);
    }
  };

  if (!data) return null;

  return (
    <Card className="p-5 shadow-premium mt-4 sm:mt-5" data-testid="card-deposit-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <CreditCard className="h-4 w-4" />Deposit by card
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 max-w-xl leading-relaxed">
            Your card is charged in {data.currency} and your USD wallet is credited instantly.
            The {data.fee_pct}% card fee is included in every figure below — nothing is added later,
            and we email you a receipt with the rate and fee as soon as it clears.
          </p>
        </div>
        <Badge variant="secondary" className="gap-1 text-[10px]">
          <ShieldCheck className="h-3 w-3" />Hosted checkout · card details never touch Tharwy
        </Badge>
      </div>

      <div className="mt-4 grid gap-2.5 sm:grid-cols-3" data-testid="card-deposit-packages">
        {data.packages.map((p) => (
          <button key={p.package_id} onClick={() => pay(p)} disabled={!!busy}
            data-testid={`card-package-${p.package_id}`}
            className="rounded-xl border p-3.5 text-start transition-colors hover:border-[color:var(--tharwy-lime)] hover:bg-accent/40 disabled:opacity-60">
            <div className="text-lg font-semibold numeric">${p.credited_usd.toLocaleString()}</div>
            <div className="text-[11px] text-muted-foreground">credited to your USD wallet</div>
            <div className="mt-2 text-sm numeric font-medium">{money(p.amount, p.currency)}</div>
            <div className="text-[11px] text-muted-foreground">
              incl. {money(p.fee_amount, p.currency)} fee · 1 USD = {p.rate} {p.currency}
            </div>
            <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-[color:var(--tharwy-lime-700)]">
              {busy === p.package_id ? "Opening checkout..." : "Pay by card"} <ArrowRight className="h-3 w-3" />
            </span>
          </button>
        ))}
      </div>
    </Card>
  );
};
